import React from "react";
import {
  Alert,
  Checkbox,
  DialogContentText,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Stack,
  Typography
} from "@mui/material";
import { gql, useMutation } from "@apollo/client";
import { Formik, Field as FormikField, FormikErrors, FormikHelpers } from "formik";
import { TextField as FmuiTextField } from "formik-mui";
import { useNotifications } from "notifications";
import { useHistory } from "react-router-dom";
import { ConfirmationDialog } from "common/ConfirmationDialog";
import { RegistrationCommitteeItem } from "registration-committee-items";
import { Permissions, useCurrentUser } from "users";
import { PRBaseStatusCode } from "practice-reviews/models";

export const DeleteRegistrationCommitteeItemMutation = gql`
  mutation DeleteRegistrationCommitteeItem($id: Int!) {
    registrationCommitteeItem {
      delete(id: $id) {
        id
      }
    }
  }
`;

const DeleteRegistrationCommitteeItemMutationAndUpdatePprpStatus = gql`
  mutation DeleteRegistrationCommitteeItemAndUpdatePprpStatus($id: Int!, $statusCode: PRBaseStatusCode!, $reasonForChange: String!) {
    registrationCommitteeItem {
      deleteAndUpdatePprpStatus(id: $id, statusCode: $statusCode, reasonForChange: $reasonForChange) {
        id
      }
    }
  }
`;

interface Props {
  item: RegistrationCommitteeItem;
  open: boolean;
  onClose: () => void;
}

type ItemWithPprpFormValues = {
  statusCode: PRBaseStatusCode;
  reasonForChange: string;
  confirmationRead: boolean;
};

const EditRegistrationCommitteeItemDeleteDialog: React.FC<Props> = ({ item, open, onClose }) => {
  const notifications = useNotifications();
  const history = useHistory();
  const { userHasPermission } = useCurrentUser();
  const doesItemRepresentPprp = !!item?.pprpProgramReview && item?.pprpProgramReview?.status?.baseStatusCode !== PRBaseStatusCode.Removed;
  const canRemovePr = userHasPermission(Permissions.PrRemove);

  // Registration Committee Item delete
  const [deleteMutate, deleteMutationState] = useMutation<{ registrationCommitteeItem: { delete: { id: number } } }, { id: number }>(
    DeleteRegistrationCommitteeItemMutation,
    {
      update(cache) {
        cache.evict({ id: cache.identify({ __typename: "RegistrationCommitteeItem", id: item.id }) });
        cache.gc();
      }
    }
  );

  // Registration Committee Item delete + update PR status (Items with attached PPRP)
  const [deleteWithPprpMutate, deleteWithPprpState] = useMutation<
    { registrationCommitteeItem: { deleteAndUpdatePprpStatus: { id: number } } },
    { id: number; statusCode: PRBaseStatusCode; reasonForChange: string }
  >(DeleteRegistrationCommitteeItemMutationAndUpdatePprpStatus, {
    update(cache) {
      cache.evict({ id: cache.identify({ __typename: "RegistrationCommitteeItem", id: item.id }) });
      cache.gc();
    }
  });

  const handleItemDelete = async () => {
    try {
      const res = await deleteMutate({ variables: { id: item.id } });
      if (res.data?.registrationCommitteeItem?.delete?.id) {
        notifications.success("Item deleted successfully.");
        onClose();
        history.push("/registration-committee-meetings");
      }
    } catch (error) {
      console.log(error);
    }
  };

  //*** DELETING ITEMS WITH ATTACHED PPRP ***//
  const initialValues: ItemWithPprpFormValues = {
    statusCode: PRBaseStatusCode.Hold,
    reasonForChange: "",
    confirmationRead: false
  };

  function validate(values: ItemWithPprpFormValues): FormikErrors<ItemWithPprpFormValues> {
    const errors: FormikErrors<ItemWithPprpFormValues> = {};
    if (values.statusCode !== PRBaseStatusCode.Hold && values.statusCode !== PRBaseStatusCode.Removed) {
      errors.statusCode = "Choose Hold or Removed.";
    }
    if (values.statusCode === PRBaseStatusCode.Removed && !canRemovePr) {
      errors.statusCode = "You do not have permission to remove a PR.";
    }
    if (values.reasonForChange.trim() === "") {
      errors.reasonForChange = "Enter the reason for this change.";
    }
    if (!values.confirmationRead) {
      errors.confirmationRead = "You must confirm that you have read and understand.";
    }
    return errors;
  }

  async function onSubmit(values: ItemWithPprpFormValues, actions: FormikHelpers<ItemWithPprpFormValues>) {
    try {
      const res = await deleteWithPprpMutate({
        variables: {
          id: item.id,
          statusCode: values.statusCode,
          reasonForChange: values.reasonForChange.trim()
        }
      });

      if (res.data?.registrationCommitteeItem?.deleteAndUpdatePprpStatus?.id) {
        notifications.success("Item deleted successfully.");
        onClose();
        history.push(`/practice-review/${item.pprpProgramReview!.prNumber}`);
      }
    } catch (error) {
      notifications.error(error);
    }
  }

  return !doesItemRepresentPprp ? (
    <ConfirmationDialog
      open={open}
      title="Delete Registration Committee Item"
      body={
        <DialogContentText>
          Are you sure you want to delete this Registration Committee Item? This action cannot be undone.
        </DialogContentText>
      }
      cancel={onClose}
      confirm={handleItemDelete}
      loading={deleteMutationState.loading}
    />
  ) : (
    <Formik initialValues={initialValues} validate={validate} onSubmit={onSubmit} validateOnMount>
      {(formik) => {
        const isLoading = deleteWithPprpState.loading;
        const confirmDisabled = !formik.isValid || !formik.values.confirmationRead || isLoading;

        return (
          <ConfirmationDialog
            open={open}
            title="Delete Registration Committee Item with PPRP"
            body={
              <Stack spacing={2}>
                <Alert severity="warning">
                  Before you continue, copy any notes or files you want to keep from this
                  <strong> Registration Committee Item</strong> into the PPRP review. Deleting the
                  <strong> Registration Committee Item</strong> permanently removes them. If you still need to move anything, cancel and do
                  that first.
                </Alert>

                {canRemovePr ? (
                  <FormikField
                    component={FmuiTextField}
                    select
                    fullWidth
                    name="statusCode"
                    label="PR Status"
                    required
                    disabled={isLoading}
                    error={formik.touched.statusCode && Boolean(formik.errors.statusCode)}
                    helperText={formik.touched.statusCode && formik.errors.statusCode}>
                    <MenuItem value={PRBaseStatusCode.Hold}>Hold</MenuItem>
                    <MenuItem value={PRBaseStatusCode.Removed}>Removed</MenuItem>
                  </FormikField>
                ) : (
                  <Stack spacing={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      PR Status
                    </Typography>
                    <Typography variant="body1">
                      <strong>Hold</strong>
                    </Typography>
                  </Stack>
                )}

                <FormikField
                  component={FmuiTextField}
                  name="reasonForChange"
                  label="Reason"
                  fullWidth
                  required
                  autoComplete="off"
                  inputProps={{ "aria-autocomplete": "none", spellCheck: "false" }}
                />

                <FormControl required error={formik.touched.confirmationRead && Boolean(formik.errors.confirmationRead)}>
                  <FormControlLabel
                    sx={{
                      alignItems: "flex-start",
                      "& .MuiFormControlLabel-label": {
                        lineHeight: 1.4,
                        marginLeft: 2
                      }
                    }}
                    control={
                      <Checkbox
                        color="primary"
                        checked={formik.values.confirmationRead}
                        onChange={(e) => formik.setFieldValue("confirmationRead", e.target.checked)}
                        onBlur={() => formik.setFieldTouched("confirmationRead", true)}
                        inputProps={{ "aria-label": "Confirm you have read and understand the warning" }}
                      />
                    }
                    label="I understand that all data associated with this Registration Committee Item will be permanently lost if I confirm deletion."
                  />
                  {formik.touched.confirmationRead && formik.errors.confirmationRead && (
                    <FormHelperText>{formik.errors.confirmationRead}</FormHelperText>
                  )}
                </FormControl>
              </Stack>
            }
            cancel={onClose}
            confirm={() => formik.submitForm()}
            loading={isLoading}
            confirmButtonText="Confirm"
            confirmButtonDisabled={confirmDisabled}
          />
        );
      }}
    </Formik>
  );
};

export default EditRegistrationCommitteeItemDeleteDialog;
